import { yupResolver } from "@hookform/resolvers/yup";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { AutoCompleteMenu, Calendar, Loader } from "@/atoms";
import { FormContainer } from "@/components";
import {
    constantsData,
    distributionTypeFormData,
    identifiersData,
    inputData,
    namesData,
    releaseDistributionFormData,
    selectionsData,
} from "@/data";
import {
    useClientsList,
    useDistributionFileTypesSelectionList,
    useDistributionPreOrderTypesSelectionList,
    useTerritoriesList,
    useTrackFormatsSelectionList,
    useTracksList,
} from "@/hooks";
import { useReleaseDistributionSchema } from "@/schemas";
import { theme } from "@/theme";
import { renderFormControllerHandler } from "@/utils";

const ReleaseDistributionForm = ({
    action,
    cancelAction,
    distributionType,
    loading,
    onSubmitForm,
    setDistributionType,
    values,
}) => {
    const [finalizedFieldsArray, setFinalizedFieldsArray] = useState([]);

    const [openPreOrderData, setOpenPreOrderData] = useState(false);

    const { t } = useTranslation();

    const schema = useReleaseDistributionSchema();

    const selections = useSelector((state) => state.selectionsReducer);

    const { id: releaseId } = useParams();

    const formMethods = useForm({
        defaultValues: {
            ...values,
            distributionType,
            isApple: false,
            isPreOrderWithIGT: false,
        },
        mode: "onChange",
        resolver: yupResolver(schema),
    });

    const {
        count: clientsCount,
        fetched: clientsFetched,
        loading: clientsLoading,
        page: clientsPage,
        refetch: clientsRefetch,
        searchKey: clientsSearchKey,
    } = useClientsList(true, true, null, true);

    const {
        count: tracksCount,
        fetched: tracksFetched,
        loading: tracksLoading,
        page: tracksPage,
        refetch: tracksRefetch,
        searchKey: tracksSearchKey,
    } = useTracksList(true, true, {}, null, parseInt(releaseId));

    const {
        count: territoriesCount,
        fetched: territoriesFetched,
        loading: territoriesLoading,
        page: territoriesPage,
        refetch: territoriesRefetch,
        searchKey: territoriesSearchKey,
    } = useTerritoriesList(true, true, null, true);

    const { loading: distributionFileTypesSelectionListLoading } = useDistributionFileTypesSelectionList();

    const { loading: distributionPreOrderTypesListLoading } = useDistributionPreOrderTypesSelectionList();

    const { loading: trackFormatsSelectionListLoading } = useTrackFormatsSelectionList();

    const {
        control,
        formState: { errors },
        setValue,
        watch,
    } = formMethods;

    const asyncMenus = {
        clients: {
            count: clientsCount,
            loading: clientsLoading,
            page: clientsPage,
            refetch: clientsRefetch,
            searchKey: clientsSearchKey,
            selectionKey: selectionsData.keys.clients,
        },
        territories: {
            count: territoriesCount,
            loading: territoriesLoading,
            page: territoriesPage,
            refetch: territoriesRefetch,
            searchKey: territoriesSearchKey,
            selectionKey: selectionsData.keys.territories,
        },
        tracks: {
            count: tracksCount,
            loading: tracksLoading,
            page: tracksPage,
            refetch: tracksRefetch,
            searchKey: tracksSearchKey,
            selectionKey: selectionsData.keys.tracks,
        },
    };

    const { preOrderWithIGT: preOrderWithIGTConstant } = constantsData;

    const {
        ids: { button: buttonId },
    } = identifiersData;

    const { closePreOrderData: closePreOrderDataButtonName, openPreOrderData: openPreOrderDataButtonName } =
        namesData.buttons;

    const {
        client: clientInputName,
        distributionType: distributionTypeInputName,
        isApple: isAppleInputName,
        isPreOrderWithIGT: isPreOrderWithIGTInputName,
        preOrderDate: preOrderDateInputName,
        preOrderType: preOrderTypeInputName,
        salesStartDate: salesStartDateInputName,
        track: trackInputName,
    } = inputData.names;

    const isApple = selections?.clients?.find(({ value }) => value === watch(clientInputName))?.isApple || false;

    const isPreOrderWithIGT = watch(preOrderTypeInputName) === preOrderWithIGTConstant;

    const salesStartDate = watch(salesStartDateInputName);

    const selectedTracksOptions = watch(trackInputName);

    const renderFormControllerChildrenHandler = (field, label, name, type, isOptional, selectionKey, isAsyncMenu) => {
        switch (type) {
            case inputData.types.date:
                return (
                    <>
                        {name === preOrderDateInputName && (
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    justifyContent: "space-between",
                                    mt: 2,
                                    cursor: "pointer",
                                }}
                                onClick={() => setOpenPreOrderData(!openPreOrderData)}
                            >
                                <Typography component="span" variant="body1">
                                    {t("labels.preOrderData")}
                                </Typography>
                                <Tooltip
                                    title={
                                        openPreOrderData
                                            ? t("actions.closePreOrderData")
                                            : t("actions.openPreOrderData")
                                    }
                                >
                                    <IconButton
                                        id={`${openPreOrderData ? closePreOrderDataButtonName : openPreOrderDataButtonName}_${buttonId}`}
                                        onClick={() => setOpenPreOrderData(!openPreOrderData)}
                                    >
                                        {openPreOrderData ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                        {!openPreOrderData && name === preOrderDateInputName ? null : (
                            <>
                                <Calendar
                                    allowedFrom={name === preOrderDateInputName ? new Date() : undefined}
                                    allowedTo={
                                        name === preOrderDateInputName && salesStartDate
                                            ? new Date(new Date(salesStartDate).getTime() - 1)
                                            : undefined
                                    }
                                    disablePast={name === preOrderDateInputName}
                                    errors={errors}
                                    field={field}
                                    hasTime={!isApple}
                                    isOptional={isOptional}
                                    label={label}
                                    name={name}
                                    value={field.value || null}
                                />
                                {name === preOrderDateInputName && !errors[preOrderDateInputName] && (
                                    <Typography
                                        color={theme.palette.grey[600]}
                                        component="p"
                                        variant="caption"
                                        sx={{
                                            mt: 1,
                                        }}
                                    >
                                        * {t("messages.inputMsgs.preOrderDateEarlierAndLater")}
                                    </Typography>
                                )}
                            </>
                        )}
                    </>
                );

            case inputData.types.multiAutoComplete:
                return (!openPreOrderData && name === trackInputName) ||
                    (openPreOrderData && name === trackInputName && !isPreOrderWithIGT) ? null : (
                    <AutoCompleteMenu
                        asyncMenuData={asyncMenus?.[selectionKey]}
                        defaultValue={values ? values[`${name}Array`] : []}
                        disabledOption={{}}
                        errors={errors}
                        filterValue={name === trackInputName ? parseInt(releaseId) : undefined}
                        filtersKeys={name === trackInputName ? ["releaseTracks", "releaseId"] : []}
                        getRenderedOption={(option) => option?.label}
                        hasFilter={name === trackInputName}
                        isAsyncMenu={isAsyncMenu}
                        isCapped={name === trackInputName}
                        isOptional={isOptional}
                        label={label}
                        maxSelectable={name === trackInputName ? Math.floor((tracksCount || 0) / 2) : undefined}
                        name={name}
                        optionLabel={(option) => option.label}
                        options={selections?.[selectionKey]}
                        selectedOptions={name === trackInputName ? selectedTracksOptions : []}
                        isMultiple
                        useSimpleTags
                        onChange={(_, options) => {
                            if (options) field.onChange(options);
                            else field.onChange([]);
                        }}
                    />
                );

            default:
                return !openPreOrderData && name === preOrderTypeInputName ? null : (
                    <AutoCompleteMenu
                        asyncMenuData={asyncMenus?.[selectionKey]}
                        errors={errors}
                        getRenderedOption={(option) => option.label}
                        isAsyncMenu={isAsyncMenu}
                        label={label}
                        name={name}
                        optionLabel={(option) => option.label}
                        options={selections?.[selectionKey]}
                        defaultValue={
                            name === distributionTypeInputName
                                ? selections?.[selectionKey]?.find(({ value }) => value === distributionType)
                                : undefined
                        }
                        onChange={(_, option) => {
                            if (name === distributionTypeInputName && option) setDistributionType(option.value);

                            if (option) field.onChange(option.value);
                            else field.onChange("");
                        }}
                    />
                );
        }
    };

    useEffect(
        () => {
            const filteredFields = isApple
                ? releaseDistributionFormData.filter((field) => !field.notApple)
                : releaseDistributionFormData.filter((field) => !field.isApple);

            setFinalizedFieldsArray([distributionTypeFormData, ...filteredFields]);
        },
        // eslint-disable-next-line
        [isApple, releaseDistributionFormData],
    );

    useEffect(
        () => {
            setValue(isAppleInputName, !!isApple, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: true,
            });

            setValue(isPreOrderWithIGTInputName, !!isPreOrderWithIGT, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: true,
            });
        },
        [isApple, isPreOrderWithIGT], // eslint-disable-line
    );

    if (
        (clientsLoading && !clientsFetched) ||
        (territoriesLoading && !territoriesFetched) ||
        (tracksLoading && !tracksFetched) ||
        distributionFileTypesSelectionListLoading ||
        distributionPreOrderTypesListLoading ||
        trackFormatsSelectionListLoading
    )
        return <Loader withoutFullHeight />;

    return (
        <FormContainer
            action={action}
            cancelAction={cancelAction}
            formMethods={formMethods}
            loading={loading}
            data={renderFormControllerHandler(control, finalizedFieldsArray, renderFormControllerChildrenHandler)}
            hasCancelButton
            withoutShadow
            onSubmitForm={onSubmitForm}
        />
    );
};

export default ReleaseDistributionForm;

ReleaseDistributionForm.propTypes = {
    action: PropTypes.string,
    cancelAction: PropTypes.func,
    distributionType: PropTypes.string,
    loading: PropTypes.bool,
    onSubmitForm: PropTypes.func,
    setDistributionType: PropTypes.func,
    values: PropTypes.object,
};
